// src/pages/RecruiterDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getRecruiterProfile, getRecruiterTokens } from '../services/recruiter';
import { getJobsByRecruiter } from '../services/jobs';
import TokenBalance from '../components/Recruiter/TokenBalance';
import JobPostings from '../components/Recruiter/JobPostings';
import PostJob from '../components/Recruiter/PostJob';
import CandidateSearch from '../components/Recruiter/CandidateSearch';
import AppliedCandidates from '../components/Recruiter/AppliedCandidates';

const RecruiterDashboard = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [recruiter, setRecruiter] = useState(null);
    const [tokens, setTokens] = useState(0);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!currentUser) {
            navigate('/login');
            return;
        }
        loadDashboardData();
    }, [currentUser]);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            const profile = await getRecruiterProfile(currentUser.uid);
            setRecruiter(profile);
            
            const tokenBalance = await getRecruiterTokens(currentUser.uid);
            setTokens(tokenBalance);
            
            const recruiterJobs = await getJobsByRecruiter(currentUser.uid);
            setJobs(recruiterJobs);
        } catch (error) {
            console.error("Error loading dashboard:", error);
        } finally {
            setLoading(false);
        }
    };

    const tabs = [
        { id: 'dashboard', name: 'Overview', icon: 'fas fa-chart-line' },
        { id: 'jobs', name: 'My Jobs', icon: 'fas fa-briefcase' },
        { id: 'post-job', name: 'Post a Job', icon: 'fas fa-plus-circle' },
        { id: 'candidates', name: 'Search Candidates', icon: 'fas fa-search' },
        { id: 'applications', name: 'Applied Candidates', icon: 'fas fa-users' },
    ];

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <i className="fas fa-spinner fa-spin text-4xl text-gradpath-green"></i>
                <span className="ml-3 text-gray-600">Loading dashboard...</span>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-2xl p-6 text-white mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold">Recruiter Dashboard</h1>
                        <p className="text-white/80 mt-1">
                            {recruiter?.companyName || recruiter?.businessType || 'Recruiter'}
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-3xl font-bold">{tokens}</div>
                        <div className="text-sm text-white/70">Tokens Available</div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-gray-200 mb-6">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2 font-medium transition-all flex items-center gap-2 ${
                            activeTab === tab.id
                                ? 'text-gradpath-green border-b-2 border-gradpath-green'
                                : 'text-gray-600 hover:text-gradpath-green'
                        }`}
                    >
                        <i className={tab.icon}></i>
                        {tab.name}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="mt-6">
                {activeTab === 'dashboard' && (
                    <div className="grid md:grid-cols-3 gap-6">
                        <TokenBalance tokens={tokens} />
                        <div className="bg-white rounded-xl shadow-md p-6">
                            <h3 className="font-bold text-gradpath-dark mb-3">Job Postings</h3>
                            <p className="text-3xl font-bold text-gradpath-green">{jobs.length}</p>
                            <p className="text-sm text-gray-500">Total jobs posted</p>
                            <div className="mt-2 text-sm">
                                <span className="text-gray-500">Active: </span>
                                <span className="font-medium text-green-600">
                                    {jobs.filter(j => new Date(j.closingDate) > new Date()).length}
                                </span>
                            </div>
                        </div>
                        <div className="bg-white rounded-xl shadow-md p-6">
                            <h3 className="font-bold text-gradpath-dark mb-3">Quick Actions</h3>
                            <button
                                onClick={() => setActiveTab('post-job')}
                                className="w-full bg-gradpath-green text-white py-2 rounded-lg hover:bg-gradpath-dark transition mb-2"
                            >
                                <i className="fas fa-plus mr-2"></i> Post a Job
                            </button>
                            <button
                                onClick={() => setActiveTab('candidates')}
                                className="w-full border border-gradpath-green text-gradpath-green py-2 rounded-lg hover:bg-gradpath-green hover:text-white transition"
                            >
                                <i className="fas fa-search mr-2"></i> Find Candidates
                            </button>
                        </div>
                    </div>
                )}
                {activeTab === 'jobs' && (
                    <JobPostings 
                        jobs={jobs} 
                        onRefresh={loadDashboardData} 
                        recruiterId={currentUser?.uid}
                    />
                )}
                {activeTab === 'post-job' && (
                    <PostJob 
                        onSuccess={loadDashboardData} 
                        recruiterId={currentUser?.uid} 
                    />
                )}
                {activeTab === 'candidates' && (
                    <CandidateSearch 
                        recruiterId={currentUser?.uid} 
                        tokens={tokens} 
                        onTokenUpdate={loadDashboardData} 
                    />
                )}
                {activeTab === 'applications' && (
                    <AppliedCandidates jobs={jobs} />
                )}
            </div>
        </div>
    );
};

export default RecruiterDashboard;